
import { defineComponent, PropType } from "vue";
import { GateReport, InvariantResult, InvariantRow } from "./types";
// EL ÍNDICE DE INVARIANTES, DE SU ÚNICA FUENTE — el mismo módulo que verifica la
// suite de backend (`tests/network-reset/invariantCatalog.spec.ts`). Aquí sólo
// se necesita el TIPO: la lista concreta la manda la consola, que sabe cuál de
// los dos gates está pintando.
import type { InvariantCatalogEntry } from "@functions/Subscriptions/Activations/migration/invariants/catalog";

const VISIBLE = 10;

/** El aspecto de cada estado, en un solo sitio: icono y chip. */
const ASPECTO: Record<string, { icono: string; chip: string; texto: string }> =
  {
    pass: {
      icono: "fa-circle-check text-success",
      chip: "bg-success",
      texto: "OK",
    },
    fail: {
      icono: "fa-circle-xmark text-danger",
      chip: "bg-danger",
      texto: "FALLA",
    },
    pending: {
      icono: "fa-circle-minus text-secondary",
      chip: "bg-secondary",
      texto: "SIN VERIFICAR",
    },
    porCorrer: {
      icono: "fa-hourglass-half text-muted",
      chip: "bg-light text-dark",
      texto: "POR CORRER",
    },
  };

export default defineComponent({
  name: "InvariantGate",
  props: {
    /**
     * El informe. `undefined` mientras no haya uno — y ESO es lo que se pinta
     * mientras el gate corre, en vez del informe de la corrida anterior.
     */
    gate: {
      type: Object as PropType<GateReport | undefined>,
      default: undefined,
    },
    /** ¿Está el gate corriendo AHORA MISMO? Lo sabe la consola, no el informe. */
    running: { type: Boolean, default: false },
    /**
     * QUÉ SE PUEDE HACER SI SALE VERDE — una línea, y distinta en cada gate: el
     * del 18 abre la validación humana, el otro abre la red. El rojo, en cambio,
     * dice lo mismo en los dos ("no se descongela"), así que ése no es prop.
     */
    okNote: {
      type: String,
      default:
        "Se puede continuar a la validación humana de los árboles y, después, al descongelamiento.",
    },
    /** Las invariantes que este gate comprueba, en su orden de ejecución. */
    catalog: {
      type: Array as PropType<InvariantCatalogEntry[]>,
      required: true,
    },
  },
  data() {
    return {
      VISIBLE,
      expanded: {} as Record<string, boolean>,
    };
  },
  computed: {
    /**
     * EL LISTADO, resuelto: el índice manda el orden y el informe manda el
     * veredicto. Lo que el informe no conteste queda en «por correr», que es
     * exactamente lo que se sabe de ello.
     */
    rows(): InvariantRow[] {
      const results = this.gate?.report?.results ?? [];
      const porId = new Map(results.map((r) => [r.id, r]));
      const enIndice = new Set(this.catalog.map((c) => c.id));

      const delIndice: InvariantRow[] = this.catalog.map((c) => {
        const result = porId.get(c.id);
        return result
          ? // El título BUENO es el del informe: es el que corrió de verdad. El
            // del índice sólo cubre el rato en que aún no hay informe.
            {
              id: c.id,
              title: result.title || c.title,
              estado: result.status,
              result,
            }
          : { id: c.id, title: c.title, estado: "porCorrer" };
      });

      // Las que llegaron y el índice no conocía. `GATE-LOAD` es la de siempre:
      // el motor la añade sólo cuando el gate se ha leído una base vacía.
      const extras: InvariantRow[] = results
        .filter((r) => !enIndice.has(r.id))
        .map((r) => ({
          id: r.id,
          title: r.title,
          estado: r.status,
          result: r,
          fueraDelIndice: true,
        }));

      return [...delIndice, ...extras];
    },
    /**
     * Los cuatro conteos, contados SOBRE LAS FILAS y no sobre `gate.summary`.
     *
     * El resumen del backend no conoce «por correr» —nace cuando el informe ya
     * existe—, y son justo los números que tienen que cuadrar con lo que se ve.
     */
    conteo(): Record<string, number> {
      const c = { pass: 0, fail: 0, pending: 0, porCorrer: 0 };
      for (const row of this.rows) c[row.estado] += 1;
      return c;
    },
    /** El pie de la derecha: cada gate cuenta una cosa distinta, o ninguna. */
    pie(): string {
      const loaded = this.gate?.loaded;
      if (loaded) {
        return (
          `${loaded.users} users · ${loaded.binaryNodes} binario · ` +
          `${loaded.unilevelNodes} unilevel`
        );
      }
      const storage = this.gate?.storage;
      if (storage) return storage.bucket ?? "sin bucket que mirar";
      return "";
    },
  },
  methods: {
    violaciones(row: InvariantRow): string[] {
      return row.result?.violations ?? [];
    },
    shown(row: InvariantRow): string[] {
      const todas = this.violaciones(row);
      return this.expanded[row.id] ? todas : todas.slice(0, VISIBLE);
    },
    toggle(id: string): void {
      this.expanded = { ...this.expanded, [id]: !this.expanded[id] };
    },
    iconoDe(row: InvariantRow): string {
      return ASPECTO[row.estado].icono;
    },
    /**
     * El chip de la derecha. En las que fallan lleva CUÁNTAS violaciones hubo —
     * el número real, no el de la muestra que cabe en el informe.
     */
    chipDe(row: InvariantRow): { clase: string; texto: string } {
      const aspecto = ASPECTO[row.estado];
      if (row.estado !== "fail") {
        return { clase: aspecto.chip, texto: aspecto.texto };
      }
      const result = row.result as InvariantResult;
      const cuantas = result.violationCount ?? result.violations.length;
      return { clase: aspecto.chip, texto: `${cuantas}` };
    },
  },
});
